/* eslint-disable @typescript-eslint/comma-dangle */
/* eslint-disable react/no-unknown-property */
import React from 'react';
import { AgentFilterValue } from '../../hooks/agent-filter/useAgentFilter';

import * as styles from './SerpFiltersSummary.style';
import { GenericSerpSortOption } from '../../types';

const MAX_ITEMS_SUMMARY = 2;
const DEFAULT_AGENT_FILTERS = { values: [], isExplicit: false };

const isValueEmpty = (value: any) => {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

export interface FilterSummaryConfig<T> {
  key: keyof T | string;
  label?: string;
  formatValue?: (value: any, filters: Partial<T>) => any;
  maxItems?: number;
}

export interface SerpFiltersSummaryProps<T = any, V extends string = any> extends React.AriaAttributes {
  filters: Partial<T> & { agentFilters?: { values: AgentFilterValue[], isExplicit: boolean } },
  sortOptions: GenericSerpSortOption<V>[],
  isAreaManagerAgentFilter?: boolean,
  configs?: FilterSummaryConfig<T>[],
}

export const SerpFiltersSummary = <T, V extends string>({
  filters,
  'aria-label': ariaLabel = 'Sommario filtri serp',
  sortOptions,
  isAreaManagerAgentFilter = false,
  configs = [],
  ...rest
}: SerpFiltersSummaryProps<T, V>) => {
  const summaryBlocks = [];

  if (Object.keys(filters).length === 0) {
    return null;
  }

  const agentFilters = filters.agentFilters ?? DEFAULT_AGENT_FILTERS;

  if (agentFilters?.values.length > 0 && agentFilters.isExplicit) {
    const more = agentFilters.values.length - MAX_ITEMS_SUMMARY;
    const agentSummaryValue = agentFilters.values.slice(0, MAX_ITEMS_SUMMARY).map((it) => it.name)
      .concat(more > 0 ? `+${more}` : [])
      .filter((element) => !!element)
      .join(', ');

    summaryBlocks.push(
      <div key="agent-filter-summary" aria-label={`${ariaLabel} (Filtro agente)`}>
        {isAreaManagerAgentFilter ? 'AM/Broker' : 'Agenti'} &quot;<strong>{agentSummaryValue}</strong>&quot;
      </div>,
    );
  }

  if ((filters as any).q) {
    summaryBlocks.push(
      <div key="search-string-summary" aria-label={`${ariaLabel} (Stringa di ricerca)`}>
        Ricerca &quot;<strong>{(filters as any).q}</strong>&quot;
      </div>,
    );
  }

  const sortOption = sortOptions.find((option) => option.key === (filters as any).sort);

  if ((filters as any).sort && sortOption) {
    summaryBlocks.push(
      <div key="sort-summary" aria-label={`${ariaLabel} (Tipo di ordinamento)`}>
        Ordinamento &quot;<strong>{sortOption.label}</strong>&quot;
      </div>,
    );
  }

  configs.forEach((config) => {
    const rawValue = (filters as any)[config.key as string];

    if (isValueEmpty(rawValue)) {
      return;
    }
    const processedValue = config.formatValue ? config.formatValue(rawValue, filters) : rawValue;

    if (isValueEmpty(processedValue)) {
      return;
    }

    let finalValue: React.ReactNode = processedValue;

    if (Array.isArray(processedValue)) {
      const max = config.maxItems ?? MAX_ITEMS_SUMMARY;
      const validItems = processedValue.filter((element) => element !== undefined && element !== null && element !== '');
      const more = validItems.length - max;

      finalValue = validItems
        .slice(0, max)
        .concat(more > 0 ? `+${more}` : [])
        .join(', ');
    }

    summaryBlocks.push(
      <div key={`dynamic-filter-${String(config.key)}`} aria-label={`${ariaLabel} (Filtro ${String(config.key)})`}>
        {config.label} &quot;<strong>{finalValue}</strong>&quot;
      </div>
    );
  });

  return summaryBlocks.length === 0
    ? null
    : (
      <div
        {...rest}
        aria-label={ariaLabel}
        css={styles.basic}
      >
        {summaryBlocks}
      </div>
    );
};

export default SerpFiltersSummary;
